import { useState, useEffect } from 'react';
import { examApi, userApi } from '../services/api';
import { useAuth } from './useAuth';

interface ExamType {
  exam_type_id: string;
  name: string;
  description: string;
  total_questions: number;
  exam_duration_minutes: number;
  passing_score: number;
  is_active: boolean;
}

interface CurrentSession {
  sessionId: string;
  examType: string;
  status: string;
  currentQuestion: number;
  totalQuestions: number;
  answeredQuestions: number;
  startedAt: string;
  timeRemainingMinutes: number;
}

interface ExamHistory {
  sessionId: string;
  examType: string;
  score: number;
  percentage: number;
  timeTakenMinutes: number;
  completedAt: string;
  passed: boolean;
}

export const useExam = () => {
  const { user } = useAuth();
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [currentSession, setCurrentSession] = useState<CurrentSession | null>(null);
  const [examHistory, setExamHistory] = useState<ExamHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadExamData = async () => {
    if (!user?.user_id) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      
      // 시험 유형 목록 로드
      try {
        const typesResponse = await examApi.getTypes();
        if (typesResponse.success) {
          setExamTypes((typesResponse.data as ExamType[]) || []);
        }
      } catch (err) {
        // 백엔드 연결 실패
      }

      // 현재 활성 세션 확인
      try {
        const sessionResponse = await userApi.getCurrentSession();
        if (sessionResponse.success && sessionResponse.data) {
          setCurrentSession(sessionResponse.data as CurrentSession);
        } else {
          setCurrentSession(null);
        }
      } catch (err) {
        setCurrentSession(null);
      }

      // 시험 기록 로드
      try {
        const historyResponse = await userApi.getHistory({ limit: 10 });
        if (historyResponse.success) {
          setExamHistory((historyResponse.data as any)?.history || []);
        }
      } catch (err) {
        // 백엔드 연결 실패
      }
      
    } catch (err) {

      setError(`시험 데이터를 불러오는데 실패했습니다: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const startExam = async (examType: string) => {
    if (!user?.user_id) return null;

    try {
      const response = await examApi.start(examType);
      
      if (response.success && response.data) {
        const newSession = {
          sessionId: (response.data as any).sessionId,
          examType: (response.data as any).examType,
          status: 'active',
          currentQuestion: 1,
          totalQuestions: (response.data as any).totalQuestions,
          answeredQuestions: 0,
          startedAt: (response.data as any).startedAt,
          timeRemainingMinutes: (response.data as any).examDurationMinutes || 130
        };
        setCurrentSession(newSession);
        return response.data;
      } else if (response.error?.code === 'ACTIVE_SESSION_EXISTS') {
        // 활성 세션이 있으니 현재 세션 정보를 다시 로드하고 해당 세션으로 이동
        await loadExamData();
        if (currentSession) {
          return { sessionId: currentSession.sessionId };
        }
        return null;
      }
      return null;
    } catch (err) {

      setError('시험을 시작하는데 실패했습니다.');
      return null;
    }
  };

  useEffect(() => {
    if (user?.user_id) {
      loadExamData();
    }
  }, [user?.user_id]);

  return {
    examTypes,
    currentSession,
    examHistory,
    isLoading,
    error,
    startExam,
    refreshData: () => {
      if (user?.user_id) {
        loadExamData();
      }
    },
  };
};
